/**
 * The technician's R3 safety pieces (slice S10): the safety check before
 * electrical work and Check on me (B37), emergency steps (B38, C13) and the
 * personal SOS (B41) — the response shapes, the query keys and the refresh
 * every write does.
 *
 * Shapes: backend/routers/tech_emergency.py (services/tech_safety_check,
 * tech_emergency, tech_sos).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '@/utils/api';
import { invalidateJobs } from './techApi';

// --- B37 safety check -------------------------------------------------------

export type SafetyStepKey = 'permit_signed' | 'isolate' | 'lock' | 'test_dead' | 'earth' | 'ppe';

export interface SafetyStep {
  key: SafetyStepKey; label: string; needs: 'photo' | 'lock' | null;
  done: boolean; done_at: string | null; done_label: string | null;
  next: boolean; blocked: string | null;
  permit?: { id: string; number: string; state: string } | null;
  lock?: { id: string; lock_no: string; tag_no: string | null; isolation_point: string; removed: boolean };
}

export interface SafetyCheckView {
  required: boolean; work_order_id: string; site_timezone: string;
  voltage_class?: string | null; done?: boolean; done_at?: string | null; done_label?: string | null;
  /** "The job was paused at 10:42 IST. Do the safety check again" — or null. */
  again?: string | null;
  steps?: SafetyStep[]; next?: SafetyStepKey | null;
  /** Why no step can be done now (on hold, finished), or null. */
  why_not?: string | null;
  my_open_locks?: { id: string; lock_no: string; tag_no: string | null; isolation_point: string }[];
}

export interface CheckOnMe {
  id: string; active: boolean; interval_min: number; work_order_id: string | null; building_id: string;
  site_timezone: string; next_due_at: string; next_due_label: string;
  last_ack_at: string | null; last_ack_label: string | null; missed: boolean;
}

// --- B38 emergency steps ----------------------------------------------------

export type EmergencyKind = 'power_failure' | 'lift_entrapment' | 'fire_panel';
export type StepType = 'time' | 'text' | 'tick' | 'logbook';

export interface EmergencyStep {
  key: string; label: string; type: StepType; done: boolean;
  value: string | boolean | { written: boolean; words: string; logbook?: string } | null;
  value_label: string | null; done_at: string | null; done_label: string | null; blocked: string | null;
}

export interface LiftLogbook { id: string; name: string; asset_id: string | null }

export interface EmergencyView {
  id: string; ref: string; kind: EmergencyKind; kind_label: string; title: string;
  building_id: string; site: string | null; site_timezone: string; asset_id: string | null;
  technician: string | null; mine: boolean; status: string; open: boolean;
  started_at: string | null; started_label: string | null;
  steps: EmergencyStep[]; left: string[];
  /** Held by a later emergency: every step is refused until it is resumed. */
  held?: boolean; held_reason?: string | null;
  reset_allowed: boolean; reset_allowed_label: string | null;
  lift_logbook_id: string | null; lift_logbooks: LiftLogbook[];
}

export interface EmergencyStarted extends EmergencyView {
  paused: { kind: 'wo' | 'ticket'; id: string; ref: string; words: string } | null;
  told: number;
}

export interface EmergencyPageData {
  kinds: { value: EmergencyKind; label: string; steps: string[] }[];
  sites: { id: string; name: string; timezone: string | null; lift_logbooks: LiftLogbook[] }[];
  open: EmergencyView[];
}

export interface FmEmergencyList { open: EmergencyView[]; closed: EmergencyView[] }

// --- B41 SOS ----------------------------------------------------------------

export interface SosView {
  id: string; person: string; mine: boolean; building_id: string | null; site: string | null;
  site_timezone: string; raised_at: string; raised_label: string; place: string;
  acknowledged: boolean; acknowledged_by: string | null; acknowledged_at: string | null;
  /** "Seen by Lena Patil at 22:14 IST", or null while unanswered. */
  seen_label: string | null;
  /** On the POST: how many were told (null when it was the same SOS again). */
  told?: number | null;
}

export interface SosInbox { open: SosView[]; answered: SosView[] }

export const EMERGENCY_KEYS = {
  safety: (woId: string) => ['tech', 'safety-check', woId] as const,
  checkOnMe: ['tech', 'check-on-me'] as const,
  emergencies: ['tech', 'emergencies'] as const,
  emergency: (id: string) => ['tech', 'emergency', id] as const,
  sos: ['tech', 'sos'] as const,
  fmSos: ['fm', 'sos'] as const,
  fmEmergencies: ['fm', 'emergencies'] as const,
};

export function useSafetyCheck(woId: string, enabled: boolean) {
  return useQuery<SafetyCheckView>({ queryKey: EMERGENCY_KEYS.safety(woId), enabled,
    queryFn: () => apiGet(`/tech/jobs/wo/${woId}/safety-check`) });
}

export function useCheckOnMe(enabled: boolean) {
  return useQuery<CheckOnMe | null>({ queryKey: EMERGENCY_KEYS.checkOnMe, enabled,
    queryFn: () => apiGet('/tech/check-on-me'), refetchInterval: 60_000 });
}

export function useEmergencies() {
  return useQuery<EmergencyPageData>({ queryKey: EMERGENCY_KEYS.emergencies,
    queryFn: () => apiGet('/tech/emergencies'), refetchInterval: 20_000 });
}

export function useEmergency(id: string, enabled: boolean) {
  // Polled: the FM's "allow the reset" shows without a reload.
  return useQuery<EmergencyView>({ queryKey: EMERGENCY_KEYS.emergency(id), enabled,
    queryFn: () => apiGet(`/tech/emergencies/${id}`), refetchInterval: 20_000 });
}

export function useMySos(enabled: boolean) {
  return useQuery<SosView | null>({ queryKey: EMERGENCY_KEYS.sos, enabled,
    queryFn: () => apiGet('/tech/sos'),
    // While an SOS waits for an answer, look for "Seen by" every 10 s.
    refetchInterval: (q) => (q.state.data && !q.state.data.acknowledged ? 10_000 : false) });
}

export function useSosInbox(enabled: boolean) {
  return useQuery<SosInbox>({ queryKey: EMERGENCY_KEYS.fmSos, enabled,
    queryFn: () => apiGet('/fm/sos'), refetchInterval: 15_000 });
}

export function useFmEmergencies(enabled: boolean) {
  return useQuery<FmEmergencyList>({ queryKey: EMERGENCY_KEYS.fmEmergencies, enabled,
    queryFn: () => apiGet('/fm/emergencies'), refetchInterval: 20_000 });
}

/** Every S10 write re-reads every screen that shows the row: the step lists,
 *  the job (list, page, home, running bar, alerts), the lock register. */
export function useEmergencyInvalidate() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['safety-check', 'check-on-me', 'emergencies', 'emergency', 'sos', 'locks', 'handover']) {
      void qc.invalidateQueries({ queryKey: ['tech', k] });
    }
    void qc.invalidateQueries({ queryKey: ['fm', 'sos'] });
    void qc.invalidateQueries({ queryKey: ['fm', 'emergencies'] });
    void qc.invalidateQueries({ queryKey: ['fm', 'locks'] });
    invalidateJobs(qc);
  };
}
